import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail, MessageSquare, LogOut, Download, Trash2, RefreshCw, Inbox,
  CheckCircle2, Clock,
} from "lucide-react";
import { useAdminAuth } from "../lib/auth";
import {
  adminListNewsletter, adminListContact, adminStats,
  adminDeleteNewsletter, adminDeleteContact, api,
} from "../lib/api";
import { FullPageLoader } from "./AdminLogin";

const TABS = [
  { key: "newsletter", label: "Newsletter", Icon: Mail },
  { key: "contact", label: "Kontaktanfragen", Icon: MessageSquare },
];

const NL_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "confirmed", label: "Bestätigt" },
  { key: "pending", label: "Unbestätigt" },
];

export default function AdminDashboard() {
  const { user, ready, logout } = useAdminAuth();
  const [tab, setTab] = useState("newsletter");
  const [nlFilter, setNlFilter] = useState("all");
  const [stats, setStats] = useState({ newsletter: 0, newsletter_pending: 0, contact: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        adminStats(),
        tab === "newsletter" ? adminListNewsletter(nlFilter) : adminListContact(),
      ]);
      setStats(s);
      setItems(list.items || []);
    } finally {
      setLoading(false);
    }
  }, [tab, nlFilter]);

  useEffect(() => { if (ready && user) load(); }, [ready, user, load]);

  if (!ready) return <FullPageLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    if (tab === "newsletter") await adminDeleteNewsletter(id);
    else await adminDeleteContact(id);
    await load();
  };

  const exportCsv = async () => {
    const res = await api.get(`/api/admin/${tab}/export.csv`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tab}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-ink text-cream">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="font-brand text-sm sm:text-base tracking-wide">
            <span className="font-bold text-brand-olive">DOGCENTER</span>{" "}
            <span className="font-semibold text-brand-sage">Lappland</span>{" "}
            <span className="font-semibold text-brand-sage/85">Admin</span>
          </Link>
          <div className="flex items-center gap-5 text-sm text-cream/85">
            <span className="hidden sm:inline">{user.email}</span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-cream/85 hover:text-cream link-underline"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={CheckCircle2} label="Newsletter bestätigt" value={stats.newsletter} />
          <StatCard icon={Clock} label="Unbestätigt" value={stats.newsletter_pending || 0} />
          <StatCard icon={MessageSquare} label="Kontaktanfragen" value={stats.contact} />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
                tab === t.key
                  ? "bg-ink text-cream"
                  : "bg-muted/60 text-ink/80 hover:bg-muted"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.Icon size={14} /> {t.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} className="btn-outline text-xs inline-flex items-center gap-2" data-testid="admin-refresh">
              <RefreshCw size={13} /> Aktualisieren
            </button>
            <button onClick={exportCsv} className="btn-primary text-xs inline-flex items-center gap-2" data-testid="admin-export">
              <Download size={13} /> CSV-Export
            </button>
          </div>
        </div>

        {tab === "newsletter" && (
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="newsletter-filter">
            {NL_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setNlFilter(f.key)}
                className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  nlFilter === f.key
                    ? "bg-sage text-cream"
                    : "bg-muted/40 text-ink/70 hover:bg-muted"
                }`}
                data-testid={`newsletter-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white border border-line overflow-hidden shadow-soft"
        >
          {loading ? (
            <div className="p-10 text-center text-mutedfg text-sm">Lade…</div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center text-mutedfg">
              <Inbox className="mx-auto mb-3" size={28} />
              <div className="text-sm">Noch keine Einträge.</div>
            </div>
          ) : tab === "newsletter" ? (
            <NewsletterTable items={items} onDelete={remove} />
          ) : (
            <ContactTable items={items} onDelete={remove} />
          )}
        </motion.div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-line p-5 sm:p-6">
      <div className="flex items-center gap-3 text-sage">
        <Icon size={16} />
        <span className="eyebrow text-mutedfg">{label}</span>
      </div>
      <div className="mt-3 font-display text-4xl text-ink">{value}</div>
    </div>
  );
}

function NewsletterTable({ items, onDelete }) {
  return (
    <table className="w-full text-sm" data-testid="newsletter-table">
      <thead className="bg-muted/50 text-mutedfg text-xs uppercase tracking-wider">
        <tr>
          <th className="text-left px-5 py-3 font-medium">Angemeldet</th>
          <th className="text-left px-5 py-3 font-medium">E-Mail</th>
          <th className="text-left px-5 py-3 font-medium">Sprache</th>
          <th className="text-left px-5 py-3 font-medium">Status</th>
          <th className="px-5 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id} className="border-t border-line">
            <td className="px-5 py-3 text-ink/75 whitespace-nowrap">{formatDate(it.created_at)}</td>
            <td className="px-5 py-3 text-ink">{it.email}</td>
            <td className="px-5 py-3 text-mutedfg uppercase text-xs">{it.language}</td>
            <td className="px-5 py-3">
              {it.confirmed ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-sage/15 text-sage-dark px-2.5 py-1 text-[11px] font-medium"
                  title={`Bestätigt: ${formatDate(it.confirmed_at)}`}
                  data-testid="newsletter-status-confirmed"
                >
                  <CheckCircle2 size={12} /> Bestätigt
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-rose/30 text-ink/70 px-2.5 py-1 text-[11px] font-medium"
                  data-testid="newsletter-status-pending"
                >
                  <Clock size={12} /> Wartet
                </span>
              )}
            </td>
            <td className="px-5 py-3 text-right">
              <button
                onClick={() => onDelete(it.id)}
                className="text-mutedfg hover:text-rose-deep transition-colors"
                aria-label="Löschen"
                data-testid={`newsletter-delete-${it.id}`}
              >
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ContactTable({ items, onDelete }) {
  return (
    <table className="w-full text-sm" data-testid="contact-table">
      <thead className="bg-muted/50 text-mutedfg text-xs uppercase tracking-wider">
        <tr>
          <th className="text-left px-5 py-3 font-medium">Datum</th>
          <th className="text-left px-5 py-3 font-medium">Name</th>
          <th className="text-left px-5 py-3 font-medium">E-Mail</th>
          <th className="text-left px-5 py-3 font-medium">Nachricht</th>
          <th className="px-5 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id} className="border-t border-line align-top">
            <td className="px-5 py-3 text-ink/75 whitespace-nowrap">{formatDate(it.created_at)}</td>
            <td className="px-5 py-3 text-ink whitespace-nowrap">{it.name}</td>
            <td className="px-5 py-3 text-ink whitespace-nowrap">
              <a href={`mailto:${it.email}`} className="link-underline">{it.email}</a>
            </td>
            <td className="px-5 py-3 text-ink/75 max-w-md">
              <div className="line-clamp-3 whitespace-pre-wrap">{it.message}</div>
            </td>
            <td className="px-5 py-3 text-right">
              <button
                onClick={() => onDelete(it.id)}
                className="text-mutedfg hover:text-rose-deep transition-colors"
                aria-label="Löschen"
              >
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
}
